import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckSquare, Plus, Loader2, Trash2, Circle, CheckCircle2, Users, User } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700'
};

const priorityLabels = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  urgent: 'Urgente'
};

const statusColors = {
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700'
};

export default function TasksPage() {
  const { user, token, isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('personal');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('my');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', category: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tasksRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/users`, { headers }).then(r => r.json()).catch(() => [])
      ]);
      setTasks(tasksRes);
      setUsers(usersRes);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPersonalTaskDialog = () => {
    setDialogType('personal');
    setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', category: '' });
    setDialogOpen(true);
  };

  const openAssignedTaskDialog = () => {
    setDialogType('assigned');
    setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', category: '' });
    setDialogOpen(true);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title) {
      toast.error('Veuillez ajouter un titre');
      return;
    }
    if (dialogType === 'assigned' && !taskForm.assigned_to) {
      toast.error('Veuillez sélectionner un utilisateur');
      return;
    }
    setSubmitting(true);
    try {
      // Pour les tâches personnelles, assigner automatiquement à l'utilisateur courant
      const taskData = {
        ...taskForm,
        assigned_to: dialogType === 'personal' ? user.id : taskForm.assigned_to
      };
      
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(taskData)
      });
      if (res.ok) {
        toast.success(dialogType === 'assigned' ? 'Tâche assignée' : 'Tâche créée');
        setDialogOpen(false);
        setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', category: '' });
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success('Statut mis a jour');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Tache supprimee');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'my') return task.assigned_to === user?.id;
    if (filter === 'assigned') return task.created_by === user?.id && task.assigned_to && task.assigned_to !== user?.id;
    return task.status === filter;
  });

  // Mes taches = taches qui me sont assignees
  const myTasks = tasks.filter(t => t.assigned_to === user?.id);
  const taskStats = {
    total: myTasks.length,
    pending: myTasks.filter(t => t.status === 'pending').length,
    in_progress: myTasks.filter(t => t.status === 'in_progress').length,
    completed: myTasks.filter(t => t.status === 'completed').length
  };

  // Taches assignees = taches que j'ai creees et assignees a d'autres
  const assignedTasks = tasks.filter(t => t.created_by === user?.id && t.assigned_to && t.assigned_to !== user?.id);
  const assignedStats = {
    total: assignedTasks.length,
    pending: assignedTasks.filter(t => t.status === 'pending').length,
    in_progress: assignedTasks.filter(t => t.status === 'in_progress').length,
    completed: assignedTasks.filter(t => t.status === 'completed').length
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion des Tâches</h1>
          <p className="text-slate-500 mt-1">Suivez et gérez les tâches</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={openAssignedTaskDialog} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />Nouvelle tâche
            </Button>
          )}
        </div>
      </div>

      {/* Stats personnelles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow ${filter === 'my' ? 'ring-2 ring-blue-200' : ''}`} onClick={() => setFilter('my')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{taskStats.total}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Mes tâches</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-slate-400 cursor-pointer hover:shadow-md transition-shadow ${filter === 'pending' ? 'ring-2 ring-slate-200' : ''}`} onClick={() => setFilter('pending')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-xl">
                <Circle className="w-6 h-6 text-slate-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{taskStats.pending}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">En attente</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow ${filter === 'in_progress' ? 'ring-2 ring-amber-200' : ''}`} onClick={() => setFilter('in_progress')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Loader2 className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{taskStats.in_progress}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">En cours</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-shadow ${filter === 'completed' ? 'ring-2 ring-emerald-200' : ''}`} onClick={() => setFilter('completed')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{taskStats.completed}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Terminées</p>
          </CardContent>
        </Card>
      </div>


      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {isAdmin && (
          <Button variant={filter === 'assigned' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('assigned')}>Tâches assignées</Button>
        )}
        <Button variant={filter === 'pending' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('pending')}>En attente</Button>
        <Button variant={filter === 'in_progress' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('in_progress')}>En cours</Button>
        <Button variant={filter === 'completed' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('completed')}>Terminées</Button>
      </div>

      {/* Tasks List */}
      <Card>
        <CardContent className="p-0">
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucune tâche</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredTasks.map((task) => (
                <div key={task.id} className="flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors">
                  <button
                    onClick={() => handleStatusChange(task, task.status === 'completed' ? 'pending' : 'completed')}
                    className="mt-1 flex-shrink-0"
                  >
                    {task.status === 'completed' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-300 hover:text-slate-400" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : ''}`}>
                        {task.title}
                      </h3>
                      <Badge className={priorityColors[task.priority]}>{priorityLabels[task.priority]}</Badge>
                      <Badge className={statusColors[task.status]}>
                        {task.status === 'pending' ? 'En attente' : task.status === 'in_progress' ? 'En cours' : 'Terminée'}
                      </Badge>
                    </div>
                    {task.description && <p className="text-sm text-slate-500 mt-1">{task.description}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      {task.assigned_to_name && <span>Assigné à : {task.assigned_to_name}</span>}
                      {task.due_date && <span>Échéance : {format(new Date(task.due_date), 'dd MMM yyyy', { locale: fr })}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={task.status} onValueChange={(v) => handleStatusChange(task, v)}>
                      <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="completed">Terminée</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteTask(task.id)}>
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle tâche</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} rows={3} />
            </div>
            <div className={`grid gap-4 ${dialogType === 'assigned' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {dialogType === 'assigned' && (
                <div className="space-y-2">
                  <Label>Assigner à *</Label>
                  <Select value={taskForm.assigned_to} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_to: v })}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.id !== user?.id).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date d'échéance</Label>
              <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (dialogType === 'assigned' ? 'Assigner' : 'Créer')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
